import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { EditSessionDrawer } from '../components/EditSessionDrawer';
import { Nav } from '../components/Nav';
import { LoadingState } from '../components/LoadingState';
import { RestTimerPill } from '../components/RestTimerPill';
import { HoldTimer, IntervalTimer } from '../components/Timer';
import { useRestTimer } from '../lib/useRestTimer';
import {
  api,
  type DailyLog,
  type Exercise,
  type RangeDay,
  type TodayTraining,
  type TrainingSession,
} from '../lib/api';
import { shortLabel } from './Dashboard';

// Inter-exercise rest is intentionally a constant rather than a per-row
// field. Per-set rest comes from the template (60s typical); the
// transition between exercises gets a longer 90s window because CNS
// fatigue stacks across overlapping prime movers and stabilizers.
const INTER_EXERCISE_REST_SEC = 90;

export default function Training() {
  const [searchParams] = useSearchParams();
  const sessionParam = searchParams.get('session');
  const isPreview = Boolean(sessionParam);

  const [session, setSession] = useState<TrainingSession | null>(null);
  const [training, setTraining] = useState<TodayTraining | null>(null);
  const [log, setLog] = useState<DailyLog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [doneSets, setDoneSets] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState(false);
  const rest = useRestTimer();
  const [peek, setPeek] = useState<{ yesterday?: RangeDay; tomorrow?: RangeDay; saturday?: RangeDay } | null>(
    null,
  );
  // previewNav drives the prev/next day strip when the user is browsing a
  // session preview (?session=<id>). Anchored on the first day in the next
  // 14 where the session matches, falling back to today so the buttons
  // always point somewhere sensible.
  const [previewNav, setPreviewNav] = useState<{
    prev?: RangeDay;
    next?: RangeDay;
    current?: RangeDay;
  } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setDoneSets({});
    try {
      if (sessionParam) {
        const s = await api.getTrainingSession(sessionParam);
        setSession(s);
        setTraining(null);
        setLog(null);
        return;
      }
      const t = await api.getTodayTraining();
      setTraining(t);
      setSession(t.session);
      const day = await api.getToday();
      setLog(day);
      // Hydrate per-set checkboxes from the persisted store. If the session
      // was already marked complete and there are no per-set rows yet (older
      // logs from before this feature shipped), fall back to filling all
      // sets so the UI reflects the "done" state.
      const map: Record<string, boolean> = {};
      try {
        const persisted = await api.listTrainingSets(day.log_date, t.session.id);
        for (const r of persisted) map[`${r.exercise_index}_${r.set_index}`] = true;
      } catch {
        // non-fatal — keep going with whatever fallback applies below
      }
      if (
        Object.keys(map).length === 0 &&
        day.training_completed &&
        t.session.exercises
      ) {
        t.session.exercises.forEach((ex, i) => {
          for (let s = 0; s < ex.sets; s++) map[`${i}_${s}`] = true;
        });
      }
      setDoneSets(map);
    } catch (e) {
      setError(String(e));
    }
  }, [sessionParam]);

  // peek strip data: pull yesterday → next Saturday in one range request,
  // pluck the three chips we render above the title.
  useEffect(() => {
    if (sessionParam) {
      // Hide peek strip in preview mode — clutters the page.
      setPeek(null);
      return;
    }
    const today = new Date();
    const yesterday = addDays(today, -1);
    const saturday = nextSaturday(today);
    api
      .getTrainingRange(toISO(yesterday), toISO(saturday))
      .then((days) => {
        const todayISO = toISO(today);
        const tomorrowISO = toISO(addDays(today, 1));
        setPeek({
          yesterday: days.find((d) => d.date === toISO(yesterday)),
          tomorrow: days.find((d) => d.date === tomorrowISO),
          saturday: days.find((d) => d.date === toISO(saturday) && d.date !== todayISO),
        });
      })
      .catch(() => setPeek(null));
  }, [sessionParam]);

  // preview mode: pull a 21-day window (today-7 → today+13), anchor on the
  // first occurrence of the previewed session in that window, and expose
  // prev/next pointers so the user can step through the calendar.
  useEffect(() => {
    if (!sessionParam) {
      setPreviewNav(null);
      return;
    }
    let cancelled = false;
    const today = new Date();
    const start = toISO(addDays(today, -7));
    const end = toISO(addDays(today, 13));
    api
      .getTrainingRange(start, end)
      .then((days) => {
        if (cancelled) return;
        if (days.length === 0) {
          setPreviewNav(null);
          return;
        }
        const todayISO = toISO(today);
        // Prefer the first match that's *not* in the past — feels more
        // forward-looking. Fall back to any match. Fall back to today.
        let idx = days.findIndex(
          (d) => d.session.id === sessionParam && d.date >= todayISO,
        );
        if (idx < 0) idx = days.findIndex((d) => d.session.id === sessionParam);
        if (idx < 0) idx = days.findIndex((d) => d.date === todayISO);
        if (idx < 0) idx = 0;
        setPreviewNav({
          prev: idx > 0 ? days[idx - 1] : undefined,
          next: idx < days.length - 1 ? days[idx + 1] : undefined,
          current: days[idx],
        });
      })
      .catch(() => {
        if (!cancelled) setPreviewNav(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionParam]);

  useEffect(() => {
    void load();
  }, [load]);

  const markComplete = async () => {
    if (!training || !log) return;
    setBusy(true);
    try {
      const next = await api.patchDay(log.log_date, {
        training_completed: true,
        training_session_id: training.session.id,
      });
      setLog(next);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSession = async (next: TrainingSession) => {
    if (!session) return;
    const updated = await api.patchTrainingSession(session.id, next);
    setSession(updated);
    if (training) setTraining({ ...training, session: updated });
    setDoneSets({});
  };

  const resetSession = async () => {
    if (!session) return;
    const updated = await api.resetTrainingSession(session.id);
    setSession(updated);
    if (training) setTraining({ ...training, session: updated });
    setDoneSets({});
  };

  // Mark the *previewed* session as the one the user actually trained today.
  // Used when today's prescribed workout doesn't match what was done (e.g.
  // doing Tuesday's upper instead of today's Wednesday conditioning).
  const logPreviewAsToday = async () => {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const next = await api.patchDay(today, {
        training_completed: true,
        training_session_id: session.id,
      });
      // Reload today's training so the cached state reflects the swap.
      try {
        const t = await api.getTodayTraining();
        setTraining(t);
      } catch {
        // ignore — log already saved
      }
      setLog(next);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!session) {
    return (
      <>
        <div className="min-h-full flex items-center justify-center text-fg-faint pb-32">
          {error ? (
            <pre className="text-danger text-sm">{error}</pre>
          ) : (
            <LoadingState />
          )}
        </div>
        <Nav />
      </>
    );
  }

  const totalSets = (session.exercises ?? []).reduce((acc, e) => acc + e.sets, 0);
  const completedSets = Object.values(doneSets).filter(Boolean).length;
  const progressPct = totalSets > 0 ? Math.round((completedSets / totalSets) * 100) : 0;
  const dayLabel = session.day_of_week.charAt(0).toUpperCase() + session.day_of_week.slice(1);

  return (
    <>
    <div className="min-h-full max-w-md lg:max-w-2xl mx-auto px-5 py-10 lg:py-12 space-y-6 pb-32">
      <header className="space-y-3">
        <Link to="/dashboard" className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint hover:text-fg">
          ← back
        </Link>
        {!isPreview && peek && (peek.yesterday || peek.tomorrow || peek.saturday) && (
          <PeekStrip peek={peek} log={log} />
        )}
        {isPreview && previewNav && (previewNav.prev || previewNav.next) && (
          <PreviewDayNav nav={previewNav} />
        )}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2 flex-1 min-w-0">
            <h1 className="font-display text-4xl leading-[1.05]">{session.title}</h1>
            <div className="flex flex-wrap gap-1.5">
              {isPreview && <Chip tone="muted">Preview</Chip>}
              {!isPreview && training && (training.training_week_number ?? training.week_number) > 0 && (
                <Chip tone="neutral">Week {training.training_week_number ?? training.week_number}</Chip>
              )}
              <Chip tone="muted">Phase {prettyPhase(session.phase)}</Chip>
              <Chip tone="muted">{dayLabel}</Chip>
            </div>
          </div>
          <button
            onClick={() => setEditing(true)}
            className="btn-secondary text-[11px] px-3.5 py-2 shrink-0 font-mono uppercase tracking-[0.14em]"
          >
            Edit
          </button>
        </div>
        {!isPreview && totalSets > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
              <span>Progress</span>
              <span className="text-fg tabular-nums">
                {completedSets}/{totalSets}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-bg-sunken overflow-hidden">
              <div
                className="h-full rounded-full bg-good-mid transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}
      </header>

      {session.equipment && session.equipment.length > 0 && (
        <Card eyebrow="Equipment">
          <ul className="flex flex-wrap gap-1.5">
            {session.equipment.map((eq) => (
              <li key={eq}>
                <Chip tone="neutral">{eq}</Chip>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {session.notes && (
        <Card eyebrow="Notes">
          <p className="text-sm text-fg leading-relaxed">{session.notes}</p>
        </Card>
      )}

      {session.exercises && session.exercises.length > 0 && (
        <Card eyebrow="Exercises">
          {session.exercises.map((ex, i) => (
            <ExerciseRow
              key={i}
              ex={ex}
              index={i + 1}
              // Always interactive — set checkboxes + rest timer should
              // work whether the user landed here via today's prescribed
              // session or by previewing a different one. Logging the
              // session as "done" still requires an explicit button
              // press; set ticks are local until then.
              interactive
              done={(s) => !!doneSets[`${i}_${s}`]}
              onToggleSet={(s, checked) => {
                setDoneSets((prev) => ({ ...prev, [`${i}_${s}`]: checked }));
                // Persist asynchronously. Preview mode (no log/training)
                // skips the write — preview is read-only by design.
                if (!isPreview && log && session) {
                  void api
                    .toggleTrainingSet(log.log_date, session.id, i, s, checked)
                    .catch(() => {
                      // Roll the local state back so the UI matches the
                      // server. Networks fail; users shouldn't see ghost
                      // checks that vanish on the next reload.
                      setDoneSets((prev) => ({ ...prev, [`${i}_${s}`]: !checked }));
                    });
                }
                // Untoggle ("oops, not done after all") cancels any
                // in-flight rest. Same applies whether the user is
                // un-marking the set we just timed for or any other —
                // simplest, most predictable behaviour.
                if (!checked) {
                  void rest.cancel();
                  return;
                }
                // Just MARKED a set done. Two timers possible:
                //   - intra-exercise: start the per-set rest for the
                //     next set when there's one queued.
                //   - inter-exercise: when this was the last set, start
                //     a 90s window pointed at the next exercise (only
                //     if there is one).
                const isLastSet = s + 1 >= ex.sets;
                if (!isLastSet && (ex.rest_sec ?? 0) > 0) {
                  void rest.start(
                    ex.rest_sec ?? 0,
                    `${ex.name} — set ${s + 2} of ${ex.sets}`,
                  );
                  return;
                }
                if (isLastSet) {
                  const nextEx = session.exercises?.[i + 1];
                  if (nextEx) {
                    void rest.start(
                      INTER_EXERCISE_REST_SEC,
                      `Next: ${nextEx.name}`,
                    );
                  }
                }
              }}
            />
          ))}
        </Card>
      )}

      {!isPreview && log && (
        <button
          onClick={markComplete}
          disabled={busy || log.training_completed}
          className={
            // Logged is a settled fact, not a button you can press again: the
            // completion pastel, not a dimmed-out secondary.
            log.training_completed
              ? 'w-full rounded-full border border-good/25 bg-good-soft py-3.5 text-sm font-semibold text-good'
              : 'btn-primary w-full py-3.5'
          }
        >
          {log.training_completed ? '✓ Session logged' : 'Mark session complete'}
        </button>
      )}

      {isPreview && (
        <div className="space-y-2">
          <button
            onClick={logPreviewAsToday}
            disabled={busy}
            className="btn-primary w-full py-3.5"
          >
            I did this session today
          </button>
          <Link to="/training" className="btn-secondary block text-center py-3 text-sm">
            Jump to today's prescribed session
          </Link>
        </div>
      )}

      {error && <pre className="text-xs text-danger whitespace-pre-wrap break-all">{error}</pre>}

      <EditSessionDrawer
        open={editing}
        session={session}
        onClose={() => setEditing(false)}
        onSave={saveSession}
        onReset={resetSession}
      />
    </div>
    {rest.active && (
      <RestTimerPill
        active={rest.active}
        secondsRemaining={rest.secondsRemaining}
        onCancel={() => void rest.cancel()}
        onDismiss={rest.dismiss}
      />
    )}
    <Nav />
    </>
  );
}

function ExerciseRow({
  ex,
  done,
  onToggleSet,
  interactive,
  index,
}: {
  ex: Exercise;
  done: (set: number) => boolean;
  onToggleSet: (set: number, checked: boolean) => void;
  interactive: boolean;
  // 1-based position in the session — a workout is an ordered sequence, so the
  // number is real information ("where am I"), not decoration.
  index: number;
}) {
  const isInterval = (ex.work_sec ?? 0) > 0;
  return (
    <div className="space-y-2.5 py-3 border-b last:border-b-0 border-line">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2.5 min-w-0">
          <span className="font-mono text-[11px] tabular-nums text-fg-faint shrink-0">
            {String(index).padStart(2, '0')}
          </span>
          <span className="text-[15px] font-bold tracking-[-0.01em] text-fg leading-tight">
            {ex.name}
          </span>
        </div>
        <span className="font-mono text-[11px] tabular-nums shrink-0 text-right text-fg-muted">
          {ex.sets} × {ex.reps}
          {ex.load && <span className="text-fg-faint"> · {ex.load}</span>}
        </span>
      </div>
      {ex.tempo_note && (
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">{ex.tempo_note}</p>
      )}

      {ex.video_url && (
        <a
          href={ex.video_url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg transition-colors"
        >
          <span aria-hidden>▶</span> Watch demo
        </a>
      )}

      {interactive && isInterval && (
        <IntervalTimer
          workSec={ex.work_sec ?? 0}
          restSec={ex.rest_int_sec ?? 0}
          rounds={ex.sets || 1}
          exerciseName={ex.name}
        />
      )}
      {interactive && !isInterval && (ex.timer_sec ?? 0) > 0 && (
        <HoldTimer
          seconds={ex.timer_sec ?? 0}
          label={ex.sets > 1 ? `per set` : undefined}
          exerciseName={ex.name}
        />
      )}

      {interactive && !isInterval && ex.sets > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: ex.sets }).map((_, s) => {
            const checked = done(s);
            return (
              <button
                key={s}
                onClick={() => onToggleSet(s, !checked)}
                aria-pressed={checked}
                // A done set is the same solid ink pill as every other committed
                // action in the app; an outstanding one is an empty grey well.
                className={`size-10 rounded-full border font-mono text-sm tabular-nums transition-colors ${
                  checked
                    ? 'bg-accent text-accent-fg border-transparent'
                    : 'bg-bg-sunken text-fg-muted border-line hover:border-line-strong hover:text-fg'
                }`}
              >
                {s + 1}
              </button>
            );
          })}
        </div>
      )}
      {ex.rest_sec && !isInterval && (
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">rest {ex.rest_sec}s</p>
      )}
    </div>
  );
}

function prettyPhase(p: string): string {
  if (p === 'phase_1') return '1';
  if (p === 'phase_2') return '2';
  if (p === 'phase_3') return '3';
  return p;
}

function PeekStrip({
  peek,
  log,
}: {
  peek: { yesterday?: RangeDay; tomorrow?: RangeDay; saturday?: RangeDay };
  log: DailyLog | null;
}) {
  const yesterdayDone = log
    ? null // we only know today's status; yesterday status not loaded
    : null;
  // We don't load yesterday's daily_log here — keeping the peek strip
  // dependency-light. The chip is unmarked (no checkmark) until cell-level
  // completion data is wired up in tier 2.
  void yesterdayDone;

  return (
    <div className="grid grid-cols-3 gap-1.5">
      <PeekChip label="Yesterday" day={peek.yesterday} />
      <PeekChip label="Tomorrow" day={peek.tomorrow} />
      <PeekChip label="Sat" day={peek.saturday} />
    </div>
  );
}

// PreviewDayNav renders a three-cell strip — prev day, current day, next
// day — that lets the user step through the calendar one day at a time
// while previewing a session. Center cell is just an anchor (shows the
// day the URL is currently pointing at); the prev/next cells link to
// `?session=<id>` for that day.
function PreviewDayNav({
  nav,
}: {
  nav: { prev?: RangeDay; next?: RangeDay; current?: RangeDay };
}) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <DayNavCell direction="prev" day={nav.prev} />
      <DayNavCell direction="current" day={nav.current} />
      <DayNavCell direction="next" day={nav.next} />
    </div>
  );
}

function DayNavCell({
  direction,
  day,
}: {
  direction: 'prev' | 'current' | 'next';
  day?: RangeDay;
}) {
  const arrow = direction === 'prev' ? '←' : direction === 'next' ? '→' : '·';
  const label =
    direction === 'prev' ? 'Prev' : direction === 'next' ? 'Next' : 'Showing';
  if (!day) {
    return (
      <div className="text-center py-2 px-2 rounded-panel border border-line bg-bg-sunken">
        <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint">
          {label}
        </p>
        <p className="text-[11px] text-fg-muted">—</p>
      </div>
    );
  }
  const inner = (
    <>
      <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint flex items-center justify-center gap-1">
        {direction === 'prev' && <span aria-hidden>{arrow}</span>}
        {label}
        {direction === 'next' && <span aria-hidden>{arrow}</span>}
      </p>
      <p className="text-[11px] text-fg leading-tight mt-0.5">
        {fmtDayShort(day.date)} · {shortLabel(day.session.type)}
      </p>
    </>
  );
  if (direction === 'current') {
    return (
      // "You are here": a sheet of white paper with an ink edge, standing proud
      // of the two grey wells beside it. Ink text stays legible — the solid
      // black pill would swallow the labels inside.
      <div className="text-center py-2 px-2 rounded-panel border border-accent bg-bg-raised">
        {inner}
      </div>
    );
  }
  return (
    <Link
      to={`/training?session=${encodeURIComponent(day.session.id)}`}
      className="text-center py-2 px-2 rounded-panel border border-line bg-bg-sunken hover:bg-bg-raised hover:border-line-strong transition-colors"
    >
      {inner}
    </Link>
  );
}

// fmtDayShort returns "Mon 5/4" for a YYYY-MM-DD date string.
function fmtDayShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return `${wd} ${d.getMonth() + 1}/${d.getDate()}`;
}

function PeekChip({ label, day }: { label: string; day?: RangeDay }) {
  if (!day) {
    return (
      <div className="text-center py-2 px-2 rounded-panel border border-line bg-bg-sunken">
        <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint">{label}</p>
        <p className="text-[11px] text-fg-muted">—</p>
      </div>
    );
  }
  return (
    <Link
      to={`/training?session=${encodeURIComponent(day.session.id)}`}
      className="text-center py-2 px-2 rounded-panel border border-line bg-bg-sunken hover:bg-bg-raised hover:border-line-strong transition-colors"
    >
      <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-faint">{label}</p>
      <p className="text-[11px] text-fg leading-tight mt-0.5">{shortLabel(day.session.type)}</p>
    </Link>
  );
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(d.getDate() + n);
  return next;
}

function nextSaturday(from: Date): Date {
  const wd = from.getDay(); // Sun=0..Sat=6
  // If today IS Saturday, return next week's Saturday so the peek shows the
  // future, not the present.
  const diff = wd === 6 ? 7 : 6 - wd;
  return addDays(from, diff);
}
